/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import isEmpty from 'lodash.isempty';
import {showJobCreateEditor} from '@ion/applications/src/components/jobs/state';
import useFilePermissionsAction from '@ion/applications/src/hooks/useFilePermissionsAction';
import {clearDeviceSelection} from '@ion/applications/src/pages/devices/state';
import {JobStatusType} from '@ion/applications/src/pages/devices/ViewDeviceEditorUtils';
import {translate} from '@ion/core/src/locale/locale';
import {getJob} from '@ion/core/src/services/Connect';
import {
  ApplicationFeatures,
  CRUDPermissions,
  DownloadJobType
} from '@ion/core/src/types/entities';
import {CellMenu} from '@ion/dls/src/components/grid/CellMenu';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import IonTooltip from '@ion/dls/src/components/tooltip/IonTooltip';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {CancelIcon} from '@ion/dls/src/icons/CancelIcon';
import {CopyIcon} from '@ion/dls/src/icons/CopyIcon';
import {NotificationTimeIcon} from '@ion/dls/src/icons/NotificationTimeIcon';
import {PlayIcon} from '@ion/dls/src/icons/PlayIcon';
import {RetryIcon} from '@ion/dls/src/icons/RetryIcon';
import {ViewRecordIcon} from '@ion/dls/src/icons/ViewRecordIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {auditDownloadJob} from '../../../store/actions/downloadjobs';
import {
  cancelDevices,
  cancelJobs,
  displayRerunJobSearchEngineDialog
} from '../../../store/actions/jobsearchengine';
import {
  jobSearchEngineTabsSelector
} from '../../../store/selectors/jobsearchengine';
import {handleDisable, isJobTabLimitReached} from './JobSearchEngineUtils';

// defines the jobs 2.0 row editor prop definitions ...
export interface JobSearchEngineRowEditorTypes {
  job?: GridResult;
  device?: GridResult;
  hasJob?: boolean;
  hasDevice?: boolean;
  setIsRerunFailed?: Function;
  handleJobClick?: Function;
  jobsPermissions?: CRUDPermissions;
  Features?: ApplicationFeatures;
}

const useStyles = makeStyles(() => ({
  disableItem : {
    opacity : '0.5',
    '&'     : {
      pointerEvents : 'auto !important'
    }
  }
}));

export enum ROW_ACTIONS {
  RERUNFAILED = 'RerunFailed',
  CANCEL = 'Cancel',
  RERUN = 'Rerun',
  CLONE = 'Clone'
};

export enum DEVICE_STATUS {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED'
};

export const isJobCancelDisabled = (status: string): boolean => ([
  DownloadJobType.Completed,
  DownloadJobType.Failed,
  DownloadJobType.Cancelled,
  DownloadJobType.Cancelling,
  DownloadJobType.Pending
].includes(status));

export const isDeviceCancelDisabled = (progressStatus: string): boolean => (![
  DEVICE_STATUS.SUCCESS, DEVICE_STATUS.FAILED, DEVICE_STATUS.CANCELLED
].includes(progressStatus));

export const isRerunAllowed = (status: string): boolean => ([
  DownloadJobType.Completed,
  DownloadJobType.Failed
].includes(status));

export const isRerunFailedTaskAllowed = (status: string): boolean => ([
  JobStatusType.IN_PROGRESS,
  JobStatusType.PENDING,
  JobStatusType.CANCELLING,
  DownloadJobType.Cancelled
].includes(status));

export const isRerunFailedAllowed = (job, status, isJob = false) => {
  const tasks = job?.task || job?.tasks || [];
  const hasFailedDevices = tasks?.some(({counts}) => Number(counts?.failed) > 0);
  return hasFailedDevices && (isJob
    ? isRerunAllowed(status)
    : !isRerunFailedTaskAllowed(status));
};

/**
 * Defines the JobSearchEngineRowEditor component. This component contains the cell actions
 * for provisioning Jobs 2.0 grid.
 *
 * ```typescript
 *
 *   import {JobSearchEngineRowEditor} from './JobSearchEngineRowEditor';
 *
 *   <JobSearchEngineRowEditor .../>
 *
 * ```
 */
export const JobSearchEngineRowEditor = ({
  job,
  device,
  hasJob,
  Features,
  hasDevice,
  handleJobClick,
  jobsPermissions,
  setIsRerunFailed
}: JobSearchEngineRowEditorTypes) => {
  const classes              = useStyles();
  const {progressStatus}     = job || {};
  const dispatch             = useDispatch();
  const cellMenuRef          = useRef(null);
  const jobDisabled          = isJobCancelDisabled(progressStatus);
  const deviceDisabled       = isDeviceCancelDisabled(device?.status);
  const rerunAllowed         = isRerunAllowed(progressStatus);
  const rerunFailedAllowed   = isRerunFailedAllowed(job, progressStatus, true);
  const tabs                 = useSelector(jobSearchEngineTabsSelector);
  const hasMaxTabLimit       = isJobTabLimitReached(tabs);

  const [
    hasFilePermissionFeature,
    setHasFilePermissionFeature
  ] = useState(true);

  const hasAssignFilePermission = useFilePermissionsAction({
    Features,
    isEditor : true
  });

  useEffect(() => {
    const handleAccess = () => {
      if (job) {
        if (!isEmpty(hasAssignFilePermission)) {
          setHasFilePermissionFeature(handleDisable(job, Features, hasAssignFilePermission));
        }
      }
    };
    
    handleAccess();
  }, [
    hasAssignFilePermission,
    Features,
    job
  ]);

  const {
    hasAddPermission,
    hasReadPermission,
    hasUpdatePermission,
    hasDeletePermission
  } = jobsPermissions || {};

  const closeMenu = useCallback(() => {
    cellMenuRef.current.closeCellMenu(cellMenuRef.current, true);
  }, []);

  const viewJob = useCallback(() => {
    closeMenu();
    handleJobClick(job?.jobId);
  }, [job]);

  const onCancel = useCallback(() => {
    closeMenu();
    if (hasJob) {
      dispatch(cancelJobs(job));
    } else if (hasDevice) {
      dispatch(cancelDevices(job, device));
    }
  }, [hasJob, hasDevice, job, device]);

  const onDisable = useCallback(() => {
    if (hasJob) {
      return jobDisabled;
    } else if (hasDevice) {
      return !deviceDisabled;
    }
  }, [hasJob, hasDevice, jobDisabled, deviceDisabled]);

  const auditJob = useCallback(() => {
    closeMenu();
    dispatch(auditDownloadJob(job));
  }, [job]);

  const rerunJob = useCallback((reRunFailed = false) => {
    return () => {
      if (!hasFilePermissionFeature) {
        closeMenu();
        setIsRerunFailed(reRunFailed);
        dispatch(displayRerunJobSearchEngineDialog(job, reRunFailed, !reRunFailed));
      }
    };
  }, [job, hasFilePermissionFeature]);

  const cloneJob = useCallback(async() => {
    if (!hasFilePermissionFeature) {
      closeMenu();
      const jobOrig = await getJob(job?.jobId);
      dispatch(clearDeviceSelection());
      dispatch(showJobCreateEditor(null, jobOrig, true));
    }
  }, [job?.jobId, hasFilePermissionFeature]);

  return (
    <CellMenu ref={cellMenuRef} menuItems={<div>
      <Render.If condition={hasJob}>
        <CellMenuItem icon={<ViewRecordIcon size={IconSize.medium} />}
          disabled={hasMaxTabLimit || !hasReadPermission}
          onClick={viewJob}
          label={translate('jobsearchengine:grid.menu.view')} />
      </Render.If>
      {hasDeletePermission && onDisable() ? (
        <IonTooltip caption={translate(
          'jobsearchengine:facets.category.progressStatus-terms.tooltip'
        )}>
          <span>
            <CellMenuItem icon={
              <CancelIcon size={IconSize.medium}
                disable={hasDeletePermission ? onDisable() : true}
                opacity={0.5}/>
            }
            onClick={onCancel}
            disabled={hasDeletePermission ? onDisable() : true}
            label={translate('jobsearchengine:grid.menu.cancel')}/>
          </span>
        </IonTooltip>
      ) : (
        <CellMenuItem icon={
          <CancelIcon size={IconSize.medium}
            disable={hasDeletePermission ? onDisable() : true}
            opacity={0.5}/>
        }
        onClick={onCancel}
        disabled={hasDeletePermission ? onDisable() : true}
        label={translate('jobsearchengine:grid.menu.cancel')}/>
      )}
      <Render.If condition={hasJob}>
        <CellMenuItem icon={<NotificationTimeIcon />}
          onClick={auditJob}
          label={translate('jobsearchengine:grid.menu.viewAuditLog')} />
        <Render.If condition={rerunFailedAllowed}>
          <CellMenuItem icon={<RetryIcon size={IconSize.medium} />}
            title={hasFilePermissionFeature && translate('jobsearchengine:rowActions.message', {action: ROW_ACTIONS.RERUNFAILED})}
            customStyle={hasFilePermissionFeature && classes.disableItem}
            disabled={hasFilePermissionFeature || !hasUpdatePermission}
            onClick={rerunJob(true)}
            label={translate('jobsearchengine:grid.menu.rerunFailedJob')} />
        </Render.If>
        <Render.If condition={rerunAllowed}>
          <CellMenuItem icon={<PlayIcon size={IconSize.medium} />}
            title={hasFilePermissionFeature && translate('jobsearchengine:rowActions.message', {action: ROW_ACTIONS.RERUN})}
            customStyle={hasFilePermissionFeature && classes.disableItem}
            disabled={hasFilePermissionFeature || !hasUpdatePermission}
            onClick={rerunJob(false)}
            label={translate('jobsearchengine:grid.menu.rerun')} />
        </Render.If>
        <CellMenuItem icon={<CopyIcon size={IconSize.medium} color={dlsColor('grey3')} />}
          title={hasFilePermissionFeature && translate('jobsearchengine:rowActions.message', {action: ROW_ACTIONS.CLONE})}
          customStyle={hasFilePermissionFeature && classes.disableItem}
          disabled={hasFilePermissionFeature || !hasAddPermission}
          onClick={cloneJob}
          label={translate('jobsearchengine:grid.menu.clone')} />
      </Render.If>
    </div>} />
  );
};
